import { useState } from 'react';
import { APP_CONFIG } from '../constants';
import {
  initPSWURLS_beta,
  initPSWURLS,
  initACURLS,
  initAQUALIGHTURLS_beta,
  initAQUALIGHTURLS,
  initControlBoardUrls,
  initPowerBoardUrls,
  initIOBoardUrls,
  initialUpgradeFile,
  initialUpgradeACFile,
  initialUpgradeSHFile,
  initialUpgradeLCFile,
  initialUpgradeControlBoardFile,
  initialUpgradePowerFile,
  initialUpgradeIOFile,
} from '../state/initialState';
import {
  loadUrls,
  saveUrl,
  removeUrl,
  readStoredUrls,
  normaliseFirmwareUrl,
  FIRMWARE_URL_KEYS,
} from '../utils/firmwareUrlStore';

export function useUpgradeFlow() {
  const [upgradeFile, setUpgradeFile] = useState(initialUpgradeFile);
  const [upgradeACFile, setUpgradeACFile] = useState(initialUpgradeACFile);
  const [upgradeSHFile, setUpgradeSHFile] = useState(initialUpgradeSHFile);
  // Every firmware family tracks its own selection, so upgradeFile now means
  // PSW only — no other card can end up armed with a PSW image.
  const [upgradeLCFile, setUpgradeLCFile] = useState(initialUpgradeLCFile);
  const [upgradeControlBoardFile, setUpgradeControlBoardFile] = useState(initialUpgradeControlBoardFile);
  const [upgradePowerFile, setUpgradePowerFile] = useState(initialUpgradePowerFile);
  const [upgradeIOFile, setUpgradeIOFile] = useState(initialUpgradeIOFile);

  // URL lists = built-in defaults merged with any user-added URLs persisted in
  // localStorage (see utils/firmwareUrlStore).
  const [PSWUpgradeURLS, setPSWUpgradeURLS] = useState(() => loadUrls(FIRMWARE_URL_KEYS.psw, initPSWURLS));
  const [PSWUpgradeURLS_beta, setPSWUpgradeURLS_beta] = useState(() =>
    loadUrls(FIRMWARE_URL_KEYS.psw_beta, initPSWURLS_beta),
  );
  const [AquaLightUpgradeURLS, setAquaLightUpgradeURLS] = useState(() =>
    loadUrls(FIRMWARE_URL_KEYS.aqualight, initAQUALIGHTURLS),
  );
  const [AquaLightUpgradeURLS_beta, setAquaLightUpgradeURLS_beta] = useState(() =>
    loadUrls(FIRMWARE_URL_KEYS.aqualight_beta, initAQUALIGHTURLS_beta),
  );
  const [AquaChemUpgradeURLS, setAquaChemUpgradeURLS] = useState(() =>
    loadUrls(FIRMWARE_URL_KEYS.aquachem, initACURLS),
  );
  const [ControlBoardUpgradeURLS, setControlBoardUpgradeURLS] = useState(() =>
    loadUrls(FIRMWARE_URL_KEYS.controlboard, initControlBoardUrls),
  );
  const [PowerBoardUpgradeURLS, setPowerBoardUpgradeURLS] = useState(() =>
    loadUrls(FIRMWARE_URL_KEYS.powerboard, initPowerBoardUrls),
  );
  const [IOBoardUpgradeURLS, setIOBoardUpgradeURLS] = useState(() =>
    loadUrls(FIRMWARE_URL_KEYS.ioboard, initIOBoardUrls),
  );

  const [updateInProgress, setUpdateInProgress] = useState(false);
  const [updateInProgressAC, setACUpdateInProgress] = useState(false);
  const [updateInProgressSH1, setSH1UpdateInProgress] = useState(false);
  const [updateInProgressSH2, setSH2UpdateInProgress] = useState(false);
  const [updateInProgressPower, setPowerUpdateInProgress] = useState(false);
  const [updateInProgressIO, setIOUpdateInProgress] = useState(false);

  // Which firmware slot each SWC board will flash. Kept here rather than in
  // DeviceView as defence in depth: this state decides which register gets
  // flashed, so it should not sit anywhere a remount can silently reset it.
  // It was DeviceView-local until App stopped remounting the whole view tree
  // on every state change (see the Content() note in App.js). Back then the
  // selection reverted to "main" on the button press, so the command went to
  // the backup register while the card watched the main one and showed no
  // progress. That remount no longer happens; this keeps the state out of
  // reach of the next one.
  const [powerBoardSlot, setPowerBoardSlot] = useState('main');
  const [ioBoardSlot, setIOBoardSlot] = useState('main');

  // The slot a *running* upgrade was actually fired on, snapshotted at the
  // button press. Distinct from the radio selections above, which the user is
  // free to move at any time: the in-progress flag is per board, not per slot,
  // so deriving the watched register from the live radio let a mid-upgrade
  // switch point the card at the wrong register — it read the other slot's
  // latched result, re-enabling the Upgrade button during a live flash.
  const [powerUpgradeSlot, setPowerUpgradeSlot] = useState('main');
  const [ioUpgradeSlot, setIOUpgradeSlot] = useState('main');

  // Each persisted category's state setter and its built-in defaults. Lets
  // add/remove be written once instead of per-list, and keeps the store key,
  // the defaults and the state in step.
  const listSetters = {
    [FIRMWARE_URL_KEYS.psw]: setPSWUpgradeURLS,
    [FIRMWARE_URL_KEYS.psw_beta]: setPSWUpgradeURLS_beta,
    [FIRMWARE_URL_KEYS.aqualight]: setAquaLightUpgradeURLS,
    [FIRMWARE_URL_KEYS.aqualight_beta]: setAquaLightUpgradeURLS_beta,
    [FIRMWARE_URL_KEYS.aquachem]: setAquaChemUpgradeURLS,
    [FIRMWARE_URL_KEYS.controlboard]: setControlBoardUpgradeURLS,
    [FIRMWARE_URL_KEYS.powerboard]: setPowerBoardUpgradeURLS,
    [FIRMWARE_URL_KEYS.ioboard]: setIOBoardUpgradeURLS,
  };

  const listDefaults = {
    [FIRMWARE_URL_KEYS.psw]: initPSWURLS,
    [FIRMWARE_URL_KEYS.psw_beta]: initPSWURLS_beta,
    [FIRMWARE_URL_KEYS.aqualight]: initAQUALIGHTURLS,
    [FIRMWARE_URL_KEYS.aqualight_beta]: initAQUALIGHTURLS_beta,
    [FIRMWARE_URL_KEYS.aquachem]: initACURLS,
    [FIRMWARE_URL_KEYS.controlboard]: initControlBoardUrls,
    [FIRMWARE_URL_KEYS.powerboard]: initPowerBoardUrls,
    [FIRMWARE_URL_KEYS.ioboard]: initIOBoardUrls,
  };

  // Recompute the list the same way the initial state was computed, rather than
  // patching it. An incrementally-appended or -filtered copy drifts from
  // (defaults + stored) as soon as the two overlap: removing a URL the user had
  // re-added on top of a built-in default used to drop that default from the
  // picker until a reload merged it back in.
  const syncList = (key) => {
    const setList = listSetters[key];
    if (setList) setList(loadUrls(key, listDefaults[key] ?? []));
  };

  // Persist first, then resync from what was actually persisted. Trusting the
  // raw argument would desync the picker from localStorage: saveUrl rejects
  // invalid URLs and trims padded ones, and the write itself can fail.
  const addUrlForKey = (key, url) => {
    const stored = saveUrl(key, url);
    if (!stored.includes(normaliseFirmwareUrl(url))) return;
    syncList(key);
  };

  // Only user-added URLs are actually removable — a built-in default isn't
  // stored, so there is nothing to remove and loadUrls would merge it back.
  const removeFirmwareUrl = (key, url) => {
    const value = normaliseFirmwareUrl(url);
    if (!readStoredUrls(key).includes(value)) return;
    // If the write failed the URL is still persisted, so leave the list alone.
    if (removeUrl(key, value).includes(value)) return;
    syncList(key);
  };

  const addPSWUrl = (value) =>
    addUrlForKey(APP_CONFIG.BETA_VERSIONS === true ? FIRMWARE_URL_KEYS.psw_beta : FIRMWARE_URL_KEYS.psw, value);

  const addAqualightUrl = (value) =>
    addUrlForKey(
      APP_CONFIG.BETA_VERSIONS === true ? FIRMWARE_URL_KEYS.aqualight_beta : FIRMWARE_URL_KEYS.aqualight,
      value,
    );

  const addACUrl = (value) => addUrlForKey(FIRMWARE_URL_KEYS.aquachem, value);

  const addControlBoardUrl = (value) => addUrlForKey(FIRMWARE_URL_KEYS.controlboard, value);

  const addPowerUrl = (value) => addUrlForKey(FIRMWARE_URL_KEYS.powerboard, value);

  const addIOUrl = (value) => addUrlForKey(FIRMWARE_URL_KEYS.ioboard, value);

  return {
    upgradeFile,
    setUpgradeFile,
    upgradeACFile,
    setUpgradeACFile,
    upgradeSHFile,
    setUpgradeSHFile,
    upgradeLCFile,
    setUpgradeLCFile,
    upgradeControlBoardFile,
    setUpgradeControlBoardFile,
    upgradePowerFile,
    setUpgradePowerFile,
    upgradeIOFile,
    setUpgradeIOFile,
    PSWUpgradeURLS,
    PSWUpgradeURLS_beta,
    AquaLightUpgradeURLS,
    AquaLightUpgradeURLS_beta,
    AquaChemUpgradeURLS,
    ControlBoardUpgradeURLS,
    PowerBoardUpgradeURLS,
    IOBoardUpgradeURLS,
    addPSWUrl,
    addAqualightUrl,
    addACUrl,
    addControlBoardUrl,
    addPowerUrl,
    addIOUrl,
    removeFirmwareUrl,
    updateInProgress,
    setUpdateInProgress,
    updateInProgressAC,
    setACUpdateInProgress,
    updateInProgressSH1,
    setSH1UpdateInProgress,
    updateInProgressSH2,
    setSH2UpdateInProgress,
    updateInProgressPower,
    setPowerUpdateInProgress,
    updateInProgressIO,
    setIOUpdateInProgress,
    powerBoardSlot,
    setPowerBoardSlot,
    ioBoardSlot,
    setIOBoardSlot,
    powerUpgradeSlot,
    setPowerUpgradeSlot,
    ioUpgradeSlot,
    setIOUpgradeSlot,
  };
}
